import $ from "jquery";

// Interfaces for Raphael.js
interface RaphaelPaper {
  rect(x: number, y: number, width: number, height: number): RaphaelElement;
  circle(x: number, y: number, r: number): RaphaelElement;
  text(x: number, y: number, text: string): RaphaelElement;
  set(): RaphaelSet;
  setViewBox(x: number, y: number, width: number, height: number): void;
}

interface RaphaelElement {
  attr(attributes: any): RaphaelElement;
  drag(
    moveHandler: Function,
    startHandler: Function,
    endHandler: Function,
  ): void;
  node: HTMLElement;
}

interface RaphaelSet {
  push(element: RaphaelElement): void;
  drag(
    moveHandler: Function,
    startHandler: Function,
    endHandler: Function,
  ): void;
}

interface Coordinates {
  x: number;
  y: number;
}

interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ScattergramOptions {
  width?: number;
  height?: number;
}

class Scattergram {
  // Constants
  private static readonly CANVAS_WIDTH = 635;
  private static readonly CANVAS_HEIGHT = 340;
  private static readonly BASE_SIZE = Scattergram.CANVAS_WIDTH;
  private static readonly BASE_ATTRIBUTES = {
    "stroke-width": 0,
    fill: "#F6F5F0",
  };

  private static readonly CIRCLE_COUNT = 5;
  private static readonly CIRCLE_BAND_WIDTH = 100;

  private static readonly DEFAULT_LOCATION: Coordinates = { x: 0, y: 0 };

  private static readonly X_VALUE_MAX = 1000.0;
  private static readonly X_VALUE_MIN = 0.0;
  private static readonly Y_VALUE_MAX = 1000.0;
  private static readonly Y_VALUE_MIN = 0.0;
  private static readonly POINT_CIRCLE_R = 6;

  private static readonly X_LEFT_MARGIN = Scattergram.POINT_CIRCLE_R * 2;
  private static readonly X_RIGHT_MARGIN = Scattergram.POINT_CIRCLE_R * 20;
  private static readonly Y_MARGIN = Scattergram.POINT_CIRCLE_R * 2;

  private static readonly ITEM_POINT_ATTRIBUTES = {
    fill: "#F59427",
    stroke: "#F59427",
  };
  private static readonly ITEM_TEXT_ATTRIBUTES = {
    "text-anchor": "start",
    "font-size": "16",
    fill: "#333",
    stroke: "#333",
    "stroke-width": 0.1,
  };

  // Instance properties
  private canvas: JQuery;
  private magnification: number;
  private graphWidth: number;
  private graphHeight: number;
  private prevLocationValues: Coordinates | null = null;
  private canvasWidth: number;
  private canvasHeight: number;
  private paper: RaphaelPaper | null = null;
  private view: ViewBox | null = null;
  private minValues: Coordinates | null = null;
  private maxValues: Coordinates | null = null;

  constructor(
    canvas: JQuery,
    magnification: number,
    location: Coordinates | null = null,
    options: ScattergramOptions = {},
  ) {
    this.canvas = canvas;
    this.magnification = magnification;
    this.graphWidth = Scattergram.BASE_SIZE * this.magnification;
    this.graphHeight = Scattergram.BASE_SIZE * this.magnification;
    this.prevLocationValues = location;
    this.canvasWidth = options?.width || Scattergram.CANVAS_WIDTH;
    this.canvasHeight = options?.height || Scattergram.CANVAS_HEIGHT;
  }

  // Static helper functions
  private static transpose<T>(matrix: T[][]): T[][] {
    if (matrix.length === 0) return [];
    return matrix[0].map((_, colIndex) => matrix.map((row) => row[colIndex]));
  }

  private static scale(from: number, to: number, num: number): number[] {
    const result: number[] = [from];
    for (let i = 1; i < num - 1; i++) {
      result.push(from + ((to - from) * i) / (num - 1));
    }
    result.push(to);
    return result;
  }

  private static scaleVector(
    fromVec: number[],
    toVec: number[],
    num: number,
  ): number[][] {
    const zipped = fromVec.map((from, i) => [from, toVec[i]]);
    const scaled = zipped.map(([from, to]) => Scattergram.scale(from, to, num));
    return Scattergram.transpose(scaled);
  }

  private static rgbColor(r: number, g: number, b: number): string {
    const toHex = (n: number) => Math.round(n).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private static getCircleColors(): string[] {
    const colorVectors = Scattergram.scaleVector(
      [251, 245, 236],
      [255, 225, 196],
      Scattergram.CIRCLE_COUNT,
    );
    return colorVectors.map(([r, g, b]) => Scattergram.rgbColor(r, g, b));
  }

  private static circleAttributes(index: number): any {
    const circleColors = Scattergram.getCircleColors();
    return { "stroke-width": 0, fill: circleColors[index] };
  }

  public static formatStats(val: number): string {
    if (val > 10) return val.toFixed(1);
    if (val > 1) return val.toFixed(2);
    return val.toFixed(3);
  }

  // Public methods
  drawMessage(msg: string): void {
    const escapedMsg = $("<div>").text($.trim(msg)).html();
    this.canvas.html(`<div class='message'>${escapedMsg}</div>`);
  }

  drawErrorMessage(msg: string): void {
    const escapedMsg = $("<div>").text(msg).html().replace(/\n/g, "<br/>");
    this.canvas.html(`<div class='error_message'>${escapedMsg}</div>`);
  }

  initPaper(): void {
    this.canvas.empty();
    this.paper = (window as any).Raphael(
      this.canvas.attr("id"),
      Scattergram.CANVAS_WIDTH,
      this.canvasHeight,
    );
    // DO NOT CHANGE initial view box location; view box must locate at (0,0) for IE
    this.view = {
      x: 0,
      y: 0,
      width: Scattergram.CANVAS_WIDTH,
      height: this.canvasHeight,
    };
    this.setViewBox();
  }

  location(): Coordinates {
    return this.coordinatesToValues(this.centerCoordinates());
  }

  private prevCoordinates(): Coordinates {
    if (this.prevLocationValues) {
      return this.centerToPosition(
        this.valuesToCoordinates(this.prevLocationValues),
      );
    } else {
      return Scattergram.DEFAULT_LOCATION;
    }
  }

  drawPoints(points: ScattergramPoint[]): void {
    this.initPaper();
    const base = this.drawBase();

    if (points.length > 0) {
      this.minValues = {
        x: Math.min(...points.map((p) => p.x)) * 0.99,
        y: Math.min(...points.map((p) => p.y)) * 0.99,
      };
      this.maxValues = {
        x: Math.max(...points.map((p) => p.x)),
        y: Math.max(...points.map((p) => p.y)),
      };
    } else {
      this.minValues = { x: 0, y: 0 };
      this.maxValues = { x: 0, y: 0 };
    }

    points.forEach((p) => this.drawPoint(p));
    this.setView(this.prevCoordinates());
    this.enableDragging(base);
  }

  private drawBase(): RaphaelSet {
    const set = this.paper!.set();
    set.push(
      this.paper!.rect(0, 0, this.graphWidth, this.graphHeight).attr(
        Scattergram.BASE_ATTRIBUTES,
      ),
    );

    const cx = this.graphWidth;
    const cy = 0;

    for (let i = 0; i < Scattergram.CIRCLE_COUNT; i++) {
      const r =
        Scattergram.CIRCLE_BAND_WIDTH *
        (Scattergram.CIRCLE_COUNT - i) *
        this.magnification;
      set.push(
        this.paper!.circle(cx, cy, r).attr(Scattergram.circleAttributes(i)),
      );
    }

    return set;
  }

  private drawPoint(point: ScattergramPoint): void {
    const coord = this.valuesToCoordinates(point);
    const circle = this.paper!.circle(
      coord.x,
      coord.y,
      Scattergram.POINT_CIRCLE_R,
    ).attr(Scattergram.ITEM_POINT_ATTRIBUTES);

    // Add tooltip
    ($(circle.node) as any).tipsy({
      gravity: "s",
      title: () => point.label(),
    });

    const attrs = { ...Scattergram.ITEM_TEXT_ATTRIBUTES };
    (attrs as any).href = point.url();
    this.paper!.text(coord.x + 11, coord.y, point.name).attr(attrs);
  }

  private enableDragging(element: RaphaelSet): void {
    let dragging = false;
    let origX: number = 0;
    let origY: number = 0;

    element.drag(
      // move
      (dx: number, dy: number, _x: number, _y: number, _event: Event) => {
        if (!dragging) return;
        this.setView({ x: origX - dx, y: origY - dy });
      },
      // down
      (_x: number, _y: number, _event: Event) => {
        dragging = true;
        origX = this.view!.x;
        origY = this.view!.y;
      },
      // up
      (_event: Event) => {
        dragging = false;
      },
    );
  }

  private setView(coordinates: Coordinates): void {
    if (!this.view) return;

    this.view.x = this.clamp(
      coordinates.x,
      0,
      this.graphWidth - this.view.width,
    );
    this.view.y = this.clamp(
      coordinates.y,
      0,
      this.graphHeight - this.view.height,
    );
    this.setViewBox();
  }

  private setViewBox(): void {
    if (!this.paper || !this.view) return;
    this.paper.setViewBox(
      this.view.x,
      this.view.y,
      this.view.width,
      this.view.height,
    );
  }

  private clamp(value: number, min: number, max: number): number {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  private valuesToCoordinates(values: Coordinates): Coordinates {
    if (!this.minValues || !this.maxValues) {
      return Scattergram.DEFAULT_LOCATION;
    }

    const area = this.renderArea();
    const valueRatio = {
      x:
        Math.log(values.x - this.minValues.x) /
        Math.log(this.maxValues.x - this.minValues.x),
      y:
        Math.log(values.y - this.minValues.y) /
        Math.log(this.maxValues.y - this.minValues.y),
    };

    return {
      x: Scattergram.X_LEFT_MARGIN + area.x * valueRatio.x,
      y: this.graphHeight - (Scattergram.Y_MARGIN + area.y * valueRatio.y),
    };
  }

  private coordinatesToValues(coordinates: Coordinates): Coordinates {
    const area = this.renderArea();
    const coordRatio = {
      x: (coordinates.x - Scattergram.X_LEFT_MARGIN) / area.x,
      y: (area.y - (coordinates.y - Scattergram.Y_MARGIN)) / area.y,
    };

    if (this.minValues && this.maxValues) {
      return {
        x:
          this.minValues.x +
          Math.exp(
            Math.log(this.maxValues.x - this.minValues.x) * coordRatio.x,
          ),
        y:
          this.minValues.y +
          Math.exp(
            Math.log(this.maxValues.y - this.minValues.y) * coordRatio.y,
          ),
      };
    } else {
      return Scattergram.DEFAULT_LOCATION;
    }
  }

  private renderArea(): Coordinates {
    return {
      x:
        this.graphWidth -
        Scattergram.X_LEFT_MARGIN -
        Scattergram.X_RIGHT_MARGIN,
      y: this.graphHeight - Scattergram.Y_MARGIN * 2,
    };
  }

  private centerCoordinates(): Coordinates {
    if (this.view) {
      return {
        x: this.view.x + this.canvasWidth / 2,
        y: this.view.y + this.canvasHeight / 2,
      };
    } else {
      return Scattergram.DEFAULT_LOCATION;
    }
  }

  private centerToPosition(coordinates: Coordinates): Coordinates {
    return {
      x: coordinates.x - this.canvasWidth / 2,
      y: coordinates.y - this.canvasHeight / 2,
    };
  }
}

// Point class
class ScattergramPoint {
  id: string;
  name: string;
  x: number;
  y: number;
  private origX: number;
  private origY: number;
  labels: { x: string; y: string };
  protected urlTemplate: string;

  constructor(
    id: string,
    name: string,
    x: number,
    y: number,
    labels: { x: string; y: string },
    urlTemplate: string,
  ) {
    this.id = id;
    this.name = name;
    this.x = x;
    this.y = y;
    this.origX = x;
    this.origY = y;
    this.labels = labels;
    this.urlTemplate = urlTemplate;
  }

  label(): string {
    return `${this.name} ${this.labels.x}: ${Scattergram.formatStats(this.origX)} ${this.labels.y}: ${Scattergram.formatStats(this.origY)}`;
  }

  url(): string {
    return this.urlTemplate.replace("@NAME@", this.name);
  }
}

export { Scattergram, ScattergramPoint };
